import { createContext, useContext, useEffect, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from '../utils/DataAccessUtils';
import { getWalkthroughDurationMinutes } from '../utils/WalkthroughComparisonUtils';
import { getAnswerItems, getAnswerItemsKey } from '../utils/TaskItemUtils';

export interface WalkthroughScoreSummary {
	score: number | null;
	maxPossibleScore: number | null;
	percentageScore: string | null;
}

// Shaped like a walkthrough task answer — questionType plus the sub-items under their questionType's
// key — so the same display components that render the current walkthrough can render a past one.
export interface PreviousTaskAnswerSummary {
	questionType: string;
	numericAnswer: number | null;
	answer: string | null;
	minMultipleChoiceValue: number | null;
	maxMultipleChoiceValue: number | null;
	checklistAnswer?: any[];
	taskListAnswer?: any[];
	comments?: string | null;
	commentsCount?: number;
	attachments?: any[];
	attachmentsCount?: number;
	workOrders?: any[];
}

interface PreviousWalkthroughSummary {
	id: number;
	completedAt: string;
	durationMinutes: number | null;
	overall: WalkthroughScoreSummary;
	bySection: { [groupTitle: string]: WalkthroughScoreSummary };
	answersBySiteSurveyTaskTitleId: { [siteSurveyTaskTitleId: string]: PreviousTaskAnswerSummary };
}

export interface WalkthroughHistory {
	walkthroughId: number;
	// Bridges a current-walkthrough task answer to the siteSurveyTaskTitleId the API keys answers by.
	siteSurveyTaskTitleIdByTaskIdentity: { [identityKey: string]: string };
	previousWalkthroughs: PreviousWalkthroughSummary[];
	averages: {
		overall: WalkthroughScoreSummary;
		durationMinutes: number | null;
		bySection: { [groupTitle: string]: WalkthroughScoreSummary };
	};
}

export interface UseWalkthroughHistoryResult {
	history: WalkthroughHistory | null;
	loading: boolean;
	error: boolean;
}

const apiObjectToScoreSummary = (obj: any): WalkthroughScoreSummary => {
	if (!obj) return { score: null, maxPossibleScore: null, percentageScore: null };

	const score = obj.score !== null && obj.score !== undefined ? Number(obj.score) : null;
	const maxPossibleScore =
		obj.maxPossibleScore !== null && obj.maxPossibleScore !== undefined
			? Number(obj.maxPossibleScore)
			: null;
	const percentageScore =
		obj.percentageScore !== null && obj.percentageScore !== undefined
			? String(obj.percentageScore)
			: null;

	if (score !== null && !isNaN(score) && maxPossibleScore !== null && !isNaN(maxPossibleScore)) {
		return { score, maxPossibleScore, percentageScore };
	}

	return { score: null, maxPossibleScore: null, percentageScore: null };
};

const secondsToMinutes = (seconds: any): number | null => {
	if (seconds === null || seconds === undefined) return null;
	const parsed = Number(seconds);
	return isNaN(parsed) ? null : Math.round(parsed / 60);
};

// The API reports a sub-item's state as `isChecked`; mirror it onto the 'true'/'false' `answer` the
// display components read, so a past item renders exactly like a current one.
const toSubItem = (subItem) => ({
	...subItem,
	answer: nullSafeGetOrElse('answer', subItem, subItem.isChecked ? 'true' : 'false'),
});

const toTaskAnswerSummary = (apiAnswer: any): PreviousTaskAnswerSummary => {
	return {
		questionType: nullSafeGet('questionType', apiAnswer),
		[getAnswerItemsKey(apiAnswer)]: getAnswerItems(apiAnswer).map(toSubItem),
		numericAnswer:
			apiAnswer.numericAnswer !== null && apiAnswer.numericAnswer !== undefined
				? Number(apiAnswer.numericAnswer)
				: null,
		// The raw "<numeric> (<label>)" answer, which is the shape the answer cell splits itself.
		answer: nullSafeGet('answer', apiAnswer) || nullSafeGet('answerLabel', apiAnswer),
		minMultipleChoiceValue:
			apiAnswer.minMultipleChoiceValue !== null && apiAnswer.minMultipleChoiceValue !== undefined
				? Number(apiAnswer.minMultipleChoiceValue)
				: null,
		maxMultipleChoiceValue:
			apiAnswer.maxMultipleChoiceValue !== null && apiAnswer.maxMultipleChoiceValue !== undefined
				? Number(apiAnswer.maxMultipleChoiceValue)
				: null,
		comments: nullSafeGet('comments', apiAnswer),
		commentsCount: nullSafeGetOrElse('commentsCount', apiAnswer, 0),
		attachments: nullSafeGetOrElse('attachments', apiAnswer, []),
		attachmentsCount: nullSafeGetOrElse('attachmentsCount', apiAnswer, 0),
		workOrders: nullSafeGetOrElse('workOrders', apiAnswer, []),
	};
};

const TASK_IDENTITY_SEPARATOR = '||';

const toIdentityPart = (value: any): string =>
	value === null || value === undefined ? '' : String(value);

const toTextIdentityPart = (value: any): string =>
	toIdentityPart(value).trim().replace(/\s+/g, ' ');

const getTaskIdentityKey = (taskAnswer): string =>
	[
		toTextIdentityPart(nullSafeGet('groupTitle', taskAnswer)),
		toTextIdentityPart(nullSafeGet('title', taskAnswer)),
		toIdentityPart(nullSafeGetWithZero('orderingId', taskAnswer)),
	].join(TASK_IDENTITY_SEPARATOR);

const toAnswerSummariesBySiteSurveyTaskTitleId = (
	entry
): { [siteSurveyTaskTitleId: string]: PreviousTaskAnswerSummary } => {
	const answerMap = nullSafeGetOrElse('answersBySiteSurveyTaskTitleId', entry, {});
	return Object.keys(answerMap).reduce((accumulator, siteSurveyTaskTitleId) => {
		accumulator[siteSurveyTaskTitleId] = toTaskAnswerSummary(answerMap[siteSurveyTaskTitleId]);
		return accumulator;
	}, {} as { [key: string]: PreviousTaskAnswerSummary });
};

// `current` and every `previous` entry key their answers by the same siteSurveyTaskTitleId, but the
// table's rows come from the walkthrough read-one, whose `titleId` is walkthrough-salted and shares no
// key space with them. Match those rows against `current` — the same walkthrough, so title/group/
// ordering line up exactly — to get into that shared key space; every past lookup is then by id.
const toSiteSurveyTaskTitleIdByTaskIdentity = (data): { [identityKey: string]: string } => {
	const answerMap = nullSafeGetOrElse('current.answersBySiteSurveyTaskTitleId', data, {});
	return Object.keys(answerMap).reduce((accumulator, siteSurveyTaskTitleId) => {
		accumulator[getTaskIdentityKey(answerMap[siteSurveyTaskTitleId])] = siteSurveyTaskTitleId;
		return accumulator;
	}, {} as { [key: string]: string });
};

export const getPreviousTaskAnswer = (
	history,
	previous,
	taskAnswer
): PreviousTaskAnswerSummary | undefined => {
	const siteSurveyTaskTitleId = nullSafeGetOrElse(
		'siteSurveyTaskTitleIdByTaskIdentity',
		history,
		{}
	)[getTaskIdentityKey(taskAnswer)];
	if (!siteSurveyTaskTitleId) return undefined;
	return nullSafeGetOrElse('answersBySiteSurveyTaskTitleId', previous, {})[siteSurveyTaskTitleId];
};

const averageOfDurationMinutes = (previousWalkthroughs): number | null => {
	const durations = previousWalkthroughs
		.map((previous) => previous.durationMinutes)
		.filter((durationMinutes) => durationMinutes !== null && durationMinutes !== undefined);
	if (durations.length === 0) return null;
	const total = durations.reduce((sum, durationMinutes) => sum + durationMinutes, 0);
	return Math.round(total / durations.length);
};

const toScoreSummariesByGroupTitle = (sections): { [key: string]: WalkthroughScoreSummary } =>
	sections.reduce((accumulator, section) => {
		const groupTitle = nullSafeGet('groupTitle', section);
		if (groupTitle) {
			accumulator[groupTitle] = apiObjectToScoreSummary(section);
		}
		return accumulator;
	}, {} as { [key: string]: WalkthroughScoreSummary });

const toWalkthroughHistory = (data): WalkthroughHistory => {
	const previousWalkthroughs = nullSafeGetOrElse('previous', data, []).map((previous) => {
		// `durationSeconds` needs `endTime`, only stamped once every question is answered — fall back to
		// the startTime -> completedAt span the stat tile uses.
		const reportedDurationMinutes = secondsToMinutes(
			nullSafeGetWithZero('durationSeconds', previous)
		);
		return {
			id: nullSafeGet('id', previous),
			completedAt: nullSafeGet('completedAt', previous),
			durationMinutes:
				reportedDurationMinutes === null
					? getWalkthroughDurationMinutes(previous)
					: reportedDurationMinutes,
			overall: apiObjectToScoreSummary(nullSafeGet('overall', previous)),
			bySection: toScoreSummariesByGroupTitle(nullSafeGetOrElse('sections', previous, [])),
			answersBySiteSurveyTaskTitleId: toAnswerSummariesBySiteSurveyTaskTitleId(previous),
		};
	});

	const reportedAverageDurationMinutes = secondsToMinutes(
		nullSafeGetWithZero('averages.durationSeconds', data)
	);

	return {
		walkthroughId: nullSafeGet('walkthroughId', data),
		siteSurveyTaskTitleIdByTaskIdentity: toSiteSurveyTaskTitleIdByTaskIdentity(data),
		previousWalkthroughs,
		averages: {
			overall: apiObjectToScoreSummary(nullSafeGet('averages.overall', data)),
			durationMinutes:
				reportedAverageDurationMinutes === null
					? averageOfDurationMinutes(previousWalkthroughs)
					: reportedAverageDurationMinutes,
			bySection: toScoreSummariesByGroupTitle(nullSafeGetOrElse('averages.bySection', data, [])),
		},
	};
};

const useWalkthroughHistory = (
	walkthroughId: number | string,
	fetchHistory: (walkthroughId: number | string) => Promise<any>
): UseWalkthroughHistoryResult => {
	const [history, setHistory] = useState<WalkthroughHistory | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	useEffect(() => {
		if (!walkthroughId) {
			setHistory(null);
			setError(false);
			setLoading(false);
			return;
		}
		setHistory(null);
		setError(false);
		setLoading(true);
		let cancelled = false;
		fetchHistory(walkthroughId)
			.then((data) => {
				if (cancelled) return;
				setHistory(toWalkthroughHistory(data));
			})
			.catch(() => {
				if (cancelled) return;
				setHistory(null);
				setError(true);
			})
			.finally(() => {
				if (cancelled) return;
				setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [walkthroughId, fetchHistory]);

	return { history, loading, error };
};

export default useWalkthroughHistory;

export const WalkthroughHistoryContext = createContext<UseWalkthroughHistoryResult>({
	history: null,
	loading: true,
	error: false,
});

export const useWalkthroughHistoryContext = (): UseWalkthroughHistoryResult =>
	useContext(WalkthroughHistoryContext);
